import React, { useEffect, useState, useCallback } from 'react';
import { reduxForm, getFormValues, Field } from 'redux-form';
import { Table, Button, Col, Row } from 'reactstrap';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import CheckboxMarkedCircleIcon from 'mdi-react/CheckboxMarkedCircleIcon';
import CloseCircleIcon from 'mdi-react/CloseCircleIcon';
import moment from 'moment';
import { splitEvery } from 'ramda';
import assignUserToTierValidate from './assignUserToTierValidate';
import t, { partial } from '../../../util/translation/translation';
import Api from '../../../util/api';
import Loading from '../../../shared/components/custom/Loading';
import RenderFileInputField from '../../../shared/components/form/FileInput';
import FormBox from '../../../shared/components/custom/FormBox';
import { capitalizeFirstLetter } from '../../../util/functions';
import useConfig from '../../../util/useConfig';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';

const s = partial('shared');
const p = partial('UserTiers');

const UploadTierCSVPage = ({
  tierID,
  getTierUsers,
  changeTab,
  currentTier,
  reset,
}) => {
  const config = useConfig();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [csvUsers, setCSVUsers] = useState([]);
  const [sendPushNotification, setSendPushNotification] = useState(false);
  const countDelimitersOutsideQuotes = (line, delimiter) => {
    let count = 0;
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (char === delimiter && !insideQuotes) {
        count++;
      }
    }

    return count;
  };

  const splitCSVIntoLines = (csvData) => {
    const lines = [];
    let currentLine = '';
    let insideQuotes = false;

    for (let i = 0; i < csvData.length; i++) {
      const char = csvData[i];
      const nextChar = csvData[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          currentLine += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
        currentLine += char;
      } else if (
        (char === '\n' || (char === '\r' && nextChar === '\n')) &&
        !insideQuotes
      ) {
        if (currentLine.trim()) {
          lines.push(currentLine);
        }
        currentLine = '';
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
      } else {
        currentLine += char;
      }
    }
    if (currentLine.trim()) {
      lines.push(currentLine);
    }

    return lines;
  };

  const detectDelimiter = (csvData) => {
    const possibleDelimiters = [',', ';', '\t', '|'];
    const allLines = splitCSVIntoLines(csvData).filter((line) => line.trim());
    const lines = allLines.slice(0, Math.min(5, allLines.length));

    let bestDelimiter = ',';
    let maxScore = 0;

    possibleDelimiters.forEach((delimiter) => {
      const columnCounts = [];

      lines.forEach((line) => {
        if (line.trim()) {
          const count = countDelimitersOutsideQuotes(line, delimiter) + 1;
          columnCounts.push(count);
        }
      });

      if (columnCounts.length > 0) {
        const mostCommonCount = columnCounts.reduce((a, b) =>
          columnCounts.filter((v) => v === a).length >=
          columnCounts.filter((v) => v === b).length
            ? a
            : b
        );
        const consistency = columnCounts.filter(
          (count) => count === mostCommonCount
        ).length;

        const hasMultipleColumns = mostCommonCount > 1 ? 1 : 0;
        const score = consistency + hasMultipleColumns;

        if (score > maxScore) {
          maxScore = score;
          bestDelimiter = delimiter;
        }
      }
    });

    return bestDelimiter;
  };

  const checkUsers = useCallback(async () => {
    setLoading(true);
    try {
      const usernames = csvUsers.map((usr) => usr.username);
      const chunked = splitEvery(300, usernames);
      const datebaseUsers = (
        await Promise.all(
          chunked.map((partialUsernames) =>
            Api.users.findByPhoneNumbers(partialUsernames)
          )
        )
      ).reduce((tot, curr) => ({ ...tot, ...curr }), {});
      const allUsersWithMarker = csvUsers.map((usr) => {
        const foundUserCloudId = datebaseUsers[usr.username];
        return {
          ...usr,
          existsInDatebase: !!foundUserCloudId,
          cloudId: foundUserCloudId,
        };
      });
      setUsers(allUsersWithMarker);
      setLoading(false);
    } catch {
      toast.error(t('UsersTab.fetchingUsersFailed'));
      setLoading(false);
    }
  }, [csvUsers]);

  const parseCSVLine = (line, delimiter) => {
    const fields = [];
    let currentField = '';
    let insideQuotes = false;
    let i = 0;

    while (i < line.length) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          currentField += '"';
          i += 2;
        } else {
          insideQuotes = !insideQuotes;
          i++;
        }
      } else if (char === delimiter && !insideQuotes) {
        let fieldValue = currentField.trim();
        if (fieldValue.startsWith('"') && fieldValue.endsWith('"')) {
          fieldValue = fieldValue.slice(1, -1).replace(/""/g, '"');
        }
        fields.push(fieldValue);
        currentField = '';
        i++;
        while (i < line.length && (line[i] === ' ' || line[i] === '\t')) {
          i++;
        }
      } else {
        currentField += char;
        i++;
      }
    }

    // Add the last field
    let lastField = currentField.trim();
    // Remove surrounding quotes if present
    if (lastField.startsWith('"') && lastField.endsWith('"')) {
      lastField = lastField.slice(1, -1).replace(/""/g, '"');
    }
    fields.push(lastField);

    return fields;
  };

  const convertCSVToArray = (csvData) => {
    const COLUMN_NAMES = [
      'firstName',
      'lastName',
      'phone',
      'tierName',
      'dueDate',
    ];
    const DATE_FORMAT = 'YYYY-MM-DD';

    // First, detect delimiter from the first few lines
    // We need to split into lines properly first to detect delimiter
    const allLines = splitCSVIntoLines(csvData);
    const delimiter = detectDelimiter(allLines.join('\n'));

    // Skip header row
    const linesWithoutHeader = allLines.slice(1);

    const splitLines = linesWithoutHeader.map((line) => {
      return parseCSVLine(line, delimiter);
    });
    const faultyLines = splitLines
      .map((line, idx) => ({ len: line.length, idx }))
      .filter((item) => item.len !== COLUMN_NAMES.length);
    if (faultyLines.length > 0) {
      const toManyLines = faultyLines.filter(
        (line) => line.len > COLUMN_NAMES.length
      );
      const toFewLines = faultyLines.filter(
        (line) => line.len < COLUMN_NAMES.length
      );
      let totalText = '';
      if (toManyLines.length > 0) {
        const toManyLineNumbers = toManyLines.reduce(
          (tot, curr, currIdx) =>
            `${tot}${curr.idx}${currIdx !== toManyLines.length - 1 ? ',' : ''}`,
          ''
        );
        totalText += `${t('UsersTab.youHaveTooManyValuesInLines')}: ${toManyLineNumbers}. `;
      }
      if (toFewLines.length > 0) {
        const toFewLineNumbers = toFewLines.reduce(
          (tot, curr, currIdx) =>
            `${tot}${curr.idx}${currIdx !== toFewLines.length - 1 ? ',' : ''}`,
          ''
        );
        totalText += `${t('UsersTab.youHaveTooFewValuesInLines')}: ${toFewLineNumbers}.`;
      }
      toast.error(totalText);
      setUsers([]);
      return [];
    }
    const objectsArray = splitLines.map((arr) =>
      arr.reduce(
        (tot, curr, idx) => Object.assign(tot, { [COLUMN_NAMES[idx]]: curr }),
        {}
      )
    );
    // Filter out rows with empty phone numbers
    const usersWithPhone = objectsArray.filter(
      (csvUser) => csvUser.phone && csvUser.phone.trim() !== ''
    );

    const mappedUsers = usersWithPhone.map((csvUser) => {
      const onlyUsername = csvUser.phone.replace(' ', '');
      const usernameWithPhone = onlyUsername.includes('+')
        ? onlyUsername
        : `+${onlyUsername}`;
      return {
        username: usernameWithPhone,
        ...csvUser,
      };
    });
    const containsInvalidDate = mappedUsers.some(
      (item) => !moment(item.dueDate, DATE_FORMAT, true).isValid()
    );
    if (containsInvalidDate) {
      toast.error(t('UsersTab.dataContainsWrongDates'));
      setUsers([]);
      return [];
    }
    return mappedUsers;
  };
  useEffect(() => {
    if (csvUsers.length > 0) {
      checkUsers();
    }
  }, [csvUsers, checkUsers]);
  const renderClassNameRows = (disabled) => {
    if (disabled) {
      return 'listDisabled transitionDuration-03 center-list';
    }
    return 'transitionDuration-03 overflow-visible-hard';
  };
  const listUsers = () => {
    return users.map((user) => {
      const { existsInDatebase } = user;
      const phoneColor = !existsInDatebase ? { color: 'white' } : undefined;
      return (
        <tr
          key={`User-${user.username}`}
          className={renderClassNameRows(!existsInDatebase)}
        >
          <td className="checkbox-position">
            {existsInDatebase ? (
              <CheckboxMarkedCircleIcon color="#399351" />
            ) : (
              <CloseCircleIcon color="#FF4861" />
            )}
          </td>
          <td>{user.firstName}</td>
          <td>{user.lastName}</td>
          <td>
            {existsInDatebase ? (
              <Link to={`/users/edit/${user.id}`} style={phoneColor}>
                {user.username}
              </Link>
            ) : (
              <div style={phoneColor}>{user.username}</div>
            )}
          </td>
          <td>{user.tierName}</td>
          <td>{user.dueDate}</td>
        </tr>
      );
    });
  };
  const handleAddCsv = ({ file }) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      const convertedUsers = convertCSVToArray(e.target.result);
      setCSVUsers(convertedUsers);
    };
    reader.readAsText(file);
  };
  const uploadCSV = async () => {
    setLoading(true);
    try {
      const usersWithDates = users.map((usr) =>
        usr.existsInDatebase
          ? { cloudId: usr.cloudId, dueDate: usr.dueDate }
          : { phoneNumber: usr.phone, dueDate: usr.dueDate }
      );
      await Api.tiers.assignUsersFromCSV(
        {
          users: usersWithDates,
          tierID,
          sendPushNotification: sendPushNotification,
        },
        config
      );
      getTierUsers();
      changeTab('2');
      setUsers([]);
      setCSVUsers([]);
      reset();
      setLoading(false);
      toast.success(t('EditUserPage.userWasUpdated'));
    } catch {
      toast.error(p('uploadCsvFailed'));
      setLoading(false);
    }
  };
  return (
    <>
      <Loading loading={loading} />
      <div className="form mb-3">
        <Row className="width-100p">
          <Col xl={6} lg={6} md={6}>
            <Field
              name="csvFile"
              component={RenderFileInputField}
              onChange={handleAddCsv}
              accept=".csv"
            />

            {renderCheckBoxField({
              name: 'sendPushNotification',
              label: s('sendPushNotification'),
              className: 'colored-click',
              defaultChecked: false,
              input: {
                onChange: () => setSendPushNotification((prev) => !prev),
              },
            })}

            <Button
              color="primary"
              size="sm"
              disabled={!csvUsers.length}
              onClick={uploadCSV}
            >
              {s('upload')}
            </Button>
          </Col>
          <Col xl={6} lg={6} md={6}>
            <FormBox title={p('formating')}>
              <Col className="pb-3">
                <p>{p('formatingDescription')}</p>
              </Col>
            </FormBox>
            {sendPushNotification && (
              <FormBox title={p('pushNotificationContent')}>
                <Col md={11} className="pb-3 mx-2">
                  <Row className="pb-2">
                    <p>{p('pushNotificationContentTitle')}</p>
                  </Row>
                  <Row>
                    <p>
                      {p('pushNotificationContentDescription', {
                        tierName: currentTier?.name || '',
                      })}
                    </p>
                  </Row>
                </Col>
              </FormBox>
            )}
          </Col>
        </Row>
      </div>
      {users.length > 0 && (
        <div
          style={{
            display: 'flex',
            marginTop: 10,
            paddingBottom: 5,
            background: '#fff',
            borderRadius: 6,
            border: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <Table responsive striped>
            <thead>
              <tr>
                <th>{s('appMember')}</th>
                <th>{s('firstname')}</th>
                <th>{s('lastname')}</th>
                <th>{s('username')}</th>
                <th>{capitalizeFirstLetter(t('MessagesForm.tier'))}</th>
                <th>{s('endDate')}</th>
              </tr>
            </thead>
            <tbody>{listUsers()}</tbody>
          </Table>
        </div>
      )}
    </>
  );
};

const reduxF = reduxForm({
  form: 'upload_tier_csv_page',
  validate: assignUserToTierValidate,
});

const mapStateToProps = (state) => ({
  formValues: getFormValues('upload_tier_csv_page')(state),
});

export default connect(mapStateToProps)(reduxF(UploadTierCSVPage));
